import React from 'react';
import { ArrowLeft, FileText, CreditCard, User, Copyright, ShieldAlert, AlertTriangle, Scale, Globe, RefreshCw, Mail } from 'lucide-react';
import './FooterPages.css';

const TermsOfUse = ({ onBack }) => {
  return (
    <div className="footer-page">
      <button className="back-button" onClick={onBack}>
        <ArrowLeft size={20} />
        Back
      </button>
      <div className="footer-page-content legal-page">
        <div className="legal-header">
          <FileText size={40} className="legal-icon" />
          <h1>Terms and Conditions</h1>
          <div className="legal-dates">
            <span>Last Updated: January 13, 2026</span>
            <span>Effective Date: January 13, 2026</span>
          </div>
        </div>

        <section className="legal-section">
          <h2>1. Introduction and Acceptance</h2>
          <p>
            Welcome to Research Methodology Wizard ("we," "our," "us," or "the App"). These Terms and Conditions ("Terms") govern your access to and use of our web application, services, and features.
          </p>
          
          <div className="important-box">
            <p><strong>BY ACCESSING OR USING RESEARCH METHODOLOGY WIZARD, YOU AGREE TO BE BOUND BY THESE TERMS.</strong> If you do not agree to these Terms, you may not use the App.</p>
          </div>

          <div className="subsection">
            <h3>1.1 Who Can Use the App</h3>
            <ul>
              <li>You must be at least 16 years old to use Research Methodology Wizard</li>
              <li>If you are using the App on behalf of an organization (university, research institution, company), you represent that you have the authority to bind that organization to these Terms</li>
              <li>You must have the legal capacity to enter into a binding contract in your jurisdiction</li>
            </ul>
          </div>

          <div className="subsection">
            <h3>1.2 Scope of Services</h3>
            <p>Research Methodology Wizard provides:</p>
            <ul>
              <li><strong>Wizard A:</strong> A 7-step guided process to define research questions and objectives</li>
              <li><strong>Wizard B:</strong> A 5-step methodology selection wizard</li>
              <li><strong>Data Collection Wizard:</strong> Tools for designing surveys, interviews, focus groups, observations, and other data collection methods</li>
              <li><strong>Questionnaire Builder:</strong> A tool to create and distribute online questionnaires</li>
              <li><strong>Framework Builder:</strong> Generates theoretical, conceptual, analytical, and methodological frameworks</li>
              <li><strong>Final Report:</strong> Comprehensive research methodology reports with export capabilities</li>
              <li><strong>Sample Size Calculators:</strong> Multiple techniques for determining appropriate sample sizes</li>
              <li><strong>Additional Tools:</strong> Glossaries, templates, checklists, and educational resources</li>
            </ul>
          </div>
        </section>

        <section className="legal-section">
          <div className="section-header-icon">
            <CreditCard size={24} />
            <h2>2. Fees, Payments, and Subscriptions</h2>
          </div>

          <div className="subsection">
            <h3>2.1 Pricing Tiers</h3>
            <p>Research Methodology Wizard operates on a subscription-based model with the following pricing, which are subject to change at anytime at our discretion:</p>
            
            <div className="pricing-table">
              <div className="pricing-row">
                <span className="tier-name">Free Tier</span>
                <span className="tier-price">No cost, very limited use</span>
              </div>
              <div className="pricing-row">
                <span className="tier-name">Basic User</span>
                <span className="tier-price">$2.00 USD per week</span>
              </div>
              <div className="pricing-row">
                <span className="tier-name">Pro User</span>
                <span className="tier-price">$4.00 USD per month</span>
              </div>
              <div className="pricing-row">
                <span className="tier-name">Advanced User</span>
                <span className="tier-price">$6.00 USD per month</span>
              </div>
              <div className="pricing-row">
                <span className="tier-name">Super User</span>
                <span className="tier-price">$50.00 USD per year</span>
              </div>
            </div>
            <p>All prices are in United States Dollars (USD) and exclude applicable taxes unless otherwise stated.</p>
          </div>

          <div className="subsection">
            <h3>2.2 Free Trial and Limited Access</h3>
            <ul>
              <li>You may access certain Wizards up to limited steps (0-3 steps) for free before you need to upgrade</li>
              <li>Free users can view a limited preview of the Final Report (first 3 sections only)</li>
              <li>Access to full Final Reports, Framework Builder, all export formats (PDF, DOCX, PNG), advanced features, and complete Data Collection and Questionnaire Builder tools requires a paid subscription</li>
            </ul>
          </div>

          <div className="subsection">
            <h3>2.3 Subscription Auto-Renewal</h3>
            <div className="warning-box">
              <p><strong>YOUR SUBSCRIPTION WILL AUTOMATICALLY RENEW AT THE END OF EACH BILLING CYCLE UNLESS YOU CANCEL AUTO-RENEWAL.</strong></p>
            </div>
            <ul>
              <li>You may cancel auto-renewal at any time through your account settings or by contacting researchmethodologywizard@gmail.com</li>
              <li>When you cancel, your subscription will remain active until the end of the current billing period</li>
              <li>No refunds will be provided for partial billing periods after cancellation</li>
            </ul>
          </div>

          <div className="subsection">
            <h3>2.4 Payment Processing</h3>
            <ul>
              <li>Payments are processed securely through our payment gateway (e.g., Stripe)</li>
              <li>You must provide accurate and complete billing information</li>
              <li>You authorize us to charge your payment method for all fees incurred</li>
              <li>If a payment fails, we will attempt to notify you and may suspend your access until payment is received</li>
            </ul>
          </div>

          <div className="subsection">
            <h3>2.5 NON-REFUNDABLE PAYMENTS</h3>
            <div className="important-box red">
              <p><strong>ALL PAYMENTS AND SUBSCRIPTION FEES ARE STRICTLY NON-REFUNDABLE.</strong></p>
              <ul>
                <li>You will not receive a refund if you cancel your subscription mid-cycle</li>
                <li>You will not receive a refund if you do not use the service during your billing period</li>
                <li>You will not receive a refund for changes of mind or dissatisfaction with the App</li>
              </ul>
              <p><strong>BY SUBSCRIBING, YOU ACKNOWLEDGE AND AGREE THAT ALL PAYMENTS ARE FINAL AND NON-REFUNDABLE.</strong></p>
            </div>
          </div>

          <div className="subsection">
            <h3>2.6 Price Changes</h3>
            <p>We reserve the right to change our pricing at any time. For active subscribers:</p>
            <ul>
              <li>Price changes will take effect at the start of your next billing cycle</li>
              <li>We will notify you at least 30 days in advance of any price increase</li>
              <li>If you do not agree to the new pricing, you may cancel your subscription before the new price takes effect</li>
            </ul>
          </div>

          <div className="subsection">
            <h3>2.7 Taxes</h3>
            <p>You are responsible for all applicable taxes, including VAT, GST, sales tax, and other governmental charges. If we are required by law to collect taxes, we will add them to your subscription fees.</p>
          </div>
        </section>

        <section className="legal-section">
          <div className="section-header-icon">
            <User size={24} />
            <h2>3. User Accounts and Responsibilities</h2>
          </div>

          <div className="subsection">
            <h3>3.1 Account Creation</h3>
            <ul>
              <li>You must provide accurate, current, and complete information when creating your account</li>
              <li>You are responsible for maintaining the confidentiality of your password</li>
              <li>You may not share your account credentials with others</li>
              <li>Each account is for individual use only (one user per account)</li>
            </ul>
          </div>

          <div className="subsection">
            <h3>3.2 Account Security</h3>
            <ul>
              <li>Notify us immediately if you suspect unauthorized access to your account</li>
              <li>You are responsible for all activity that occurs under your account</li>
              <li>We are not liable for any loss or damage arising from unauthorized use of your account</li>
              <li>We recommend enabling two-factor authentication if available</li>
            </ul>
          </div>

          <div className="subsection">
            <h3>3.3 Account Termination</h3>
            <h4>By You:</h4>
            <ul>
              <li>You may close your account at any time through your account settings</li>
              <li>Closing your account will cancel your subscription (no refund will be provided)</li>
              <li>Your research data will be deleted within 90 days of account closure</li>
            </ul>
            <h4>By Us:</h4>
            <ul>
              <li>We may suspend or terminate your account if you violate these Terms</li>
              <li>We may terminate accounts that have been inactive for more than 12 months (with prior notice)</li>
              <li>We may terminate your account for fraudulent activity, abuse, or illegal use</li>
              <li>If we terminate your account for cause, no refund will be provided</li>
            </ul>
          </div>
        </section>

        <section className="legal-section">
          <div className="section-header-icon">
            <Copyright size={24} />
            <h2>4. Your Content and Intellectual Property</h2>
          </div>

          <div className="subsection">
            <h3>4.1 Ownership of Your Research Data</h3>
            <div className="highlight-box">
              <p><strong>You retain all intellectual property rights to your research data, projects, questionnaires, frameworks, and other content you create using the App ("Your Content").</strong></p>
              <p>We do not claim ownership of Your Content. These Terms do not transfer any ownership rights from you to us.</p>
            </div>
          </div>

          <div className="subsection">
            <h3>4.2 License to Use Your Content</h3>
            <p>By using the App, you grant us a limited, worldwide, non-exclusive license to:</p>
            <ul>
              <li>Store, process, and display Your Content to provide the App's services</li>
              <li>Backup Your Content for disaster recovery purposes</li>
              <li>Use aggregated, anonymized, and de-identified data derived from Your Content to improve our services and develop new features</li>
              <li>Share Your Content with third-party service providers (cloud hosting, analytics) as necessary to operate the App</li>
            </ul>
            <p>This license continues only for as long as you use the App and for a reasonable backup period (90 days) after account closure.</p>
          </div>

          <div className="subsection">
            <h3>4.3 Responsibility for Your Content</h3>
            <ul>
              <li>You are solely responsible for Your Content and any consequences of uploading or publishing it</li>
              <li>You represent and warrant that you have all necessary rights to Your Content</li>
              <li>You warrant that Your Content does not infringe any third party's intellectual property rights</li>
              <li>You warrant that Your Content complies with applicable laws and does not contain illegal material</li>
            </ul>
          </div>

          <div className="subsection">
            <h3>4.4 Our Intellectual Property</h3>
            <p>All software, designs, text, graphics, logos, interfaces, code, and other materials provided by Research Methodology Wizard are our intellectual property or that of our licensors. You may not:</p>
            <ul>
              <li>Copy, modify, distribute, or reverse engineer any part of the App</li>
              <li>Remove any copyright, trademark, or proprietary notices</li>
              <li>Use our trademarks, logos, or brand elements without written permission</li>
              <li>Create derivative works based on the App</li>
            </ul>
          </div>
        </section>

        <section className="legal-section">
          <div className="section-header-icon">
            <ShieldAlert size={24} />
            <h2>5. Acceptable Use Policy</h2>
          </div>

          <div className="subsection">
            <h3>5.1 Prohibited Uses</h3>
            <p>You agree not to use Research Methodology Wizard to:</p>
            
            <h4>Illegal Activities:</h4>
            <ul>
              <li>Violate any local, national, or international law or regulation</li>
              <li>Infringe upon intellectual property rights of others</li>
              <li>Collect or harvest personal information without consent</li>
              <li>Engage in fraud, phishing, or identity theft</li>
            </ul>

            <h4>Harmful Content:</h4>
            <ul>
              <li>Upload content that is defamatory, obscene, threatening, or harassing</li>
              <li>Distribute malware, viruses, or other harmful code</li>
              <li>Promote violence, terrorism, or illegal activities</li>
              <li>Share child sexual abuse material or exploit minors</li>
            </ul>

            <h4>System Abuse:</h4>
            <ul>
              <li>Attempt to gain unauthorized access to our systems or other users' accounts</li>
              <li>Use automated tools (bots, scrapers) to access the App without permission</li>
              <li>Overwhelm our servers with excessive requests (DDoS attacks)</li>
              <li>Bypass security measures or access restrictions</li>
            </ul>

            <h4>Commercial Misuse:</h4>
            <ul>
              <li>Resell, lease, or sublicense access to the App without authorization</li>
              <li>Use the App to build a competing product or service</li>
              <li>Scrape our content or data for commercial purposes</li>
            </ul>
          </div>

          <div className="subsection">
            <h3>5.2 Consequences of Violation</h3>
            <p>If you violate this Acceptable Use Policy, we may:</p>
            <ul>
              <li>Issue a warning and request that you cease the violating behavior</li>
              <li>Temporarily suspend your account</li>
              <li>Permanently terminate your account without refund</li>
              <li>Report illegal activity to law enforcement</li>
              <li>Take legal action to protect our rights</li>
            </ul>
          </div>
        </section>

        <section className="legal-section">
          <div className="section-header-icon">
            <AlertTriangle size={24} />
            <h2>6. Disclaimer: Methodological Guidance and Limitations of Liability</h2>
          </div>

          <div className="subsection">
            <h3>6.1 Educational Tool - Not Professional Advice</h3>
            <div className="warning-box">
              <p><strong>Research Methodology Wizard is an educational tool designed to guide users through research design processes.</strong></p>
            </div>
            <p>You acknowledge and agree that:</p>
            <ul>
              <li>The App does not provide professional research consulting or academic supervision</li>
              <li>The methodologies, frameworks, and sample size calculations offered are suggestions based on general principles and may not be appropriate for every research context</li>
              <li>You are solely responsible for evaluating whether the suggested methodologies are suitable for your specific research project, discipline, and institutional requirements</li>
              <li>The App cannot replace human judgment, expert consultation, or approval from your research supervisor, ethics committee, or academic institution</li>
            </ul>
          </div>

          <div className="subsection">
            <h3>6.2 Sources of Methodological Content</h3>
            <p>The methodological guidance, sample size techniques, and frameworks provided by this App are based on established research methodologies from recognized sources, including but not limited to:</p>
            <ul>
              <li><strong>Saunders, M., Lewis, P., & Thornhill, A.</strong> - Research methods for business students (Research Onion framework)</li>
              <li><strong>Creswell, J.W. & Creswell, J.D.</strong> - Research Design: Qualitative, Quantitative, and Mixed Methods Approaches</li>
              <li><strong>Sample Size Calculation Techniques</strong> including formulas from Cochran, W.G.; Krejcie, R.V. & Morgan, D.W.; Slovin's formula; Yamane's formula</li>
            </ul>
            <div className="warning-box">
              <p><strong>WHILE THESE SOURCES ARE AUTHORITATIVE AND WIDELY USED IN ACADEMIC RESEARCH, THE APP'S IMPLEMENTATION, INTERPRETATION, AND APPLICATION OF THESE METHODOLOGIES MAY CONTAIN ERRORS, OMISSIONS, OR SIMPLIFICATIONS.</strong></p>
            </div>
          </div>

          <div className="subsection">
            <h3>6.3 User Responsibility and Informed Use</h3>
            <div className="important-box">
              <p><strong>BY USING RESEARCH METHODOLOGY WIZARD, YOU EXPLICITLY ACKNOWLEDGE AND AGREE THAT:</strong></p>
              <ul>
                <li>You are responsible for verifying the appropriateness of all methodological choices for your specific research context</li>
                <li>You will consult with qualified research supervisors, advisors, or methodologists before finalizing your research design</li>
                <li>You will independently verify sample size calculations and ensure they meet the standards of your discipline and institution</li>
                <li>You understand that research methodology is a complex field requiring contextual knowledge</li>
                <li>You will use the App's outputs as a starting point or reference tool, not as a final or authoritative research plan</li>
                <li>You will comply with all ethical requirements of your institution and obtain necessary approvals (e.g., IRB/ethics committee approval)</li>
                <li>You will not rely solely on the App's outputs for critical research decisions without independent verification</li>
              </ul>
            </div>
          </div>

          <div className="subsection">
            <h3>6.4 NO LIABILITY FOR METHODOLOGICAL ERRORS OR RESEARCH OUTCOMES</h3>
            <div className="important-box red">
              <p><strong>RESEARCH METHODOLOGY WIZARD BEARS NO RESPONSIBILITY, WHETHER IMPLIED OR EXPLICIT, FOR:</strong></p>
              <ul>
                <li>Errors, inaccuracies, or omissions in the methodological guidance, sample size calculations, or frameworks provided</li>
                <li>Inappropriate methodology selection based on the App's suggestions</li>
                <li>Research projects that fail to meet academic standards or are rejected by supervisors, ethics committees, or institutions</li>
                <li>Incorrect sample sizes that result in underpowered or overpowered studies</li>
                <li>Consequences arising from misuse or misinterpretation of the App's outputs</li>
                <li>Any direct, indirect, incidental, consequential, or punitive damages resulting from your use of the App's methodological tools</li>
                <li>Loss of data, research time, academic opportunities, or professional reputation arising from use of the App</li>
              </ul>
              <p><strong>YOU EXPRESSLY ASSUME ALL RISK ASSOCIATED WITH THE USE OF THIS TOOL AND AGREE TO USE IT CAREFULLY, CRITICALLY, AND IN CONJUNCTION WITH PROFESSIONAL GUIDANCE.</strong></p>
            </div>
          </div>

          <div className="subsection">
            <h3>6.5 "AS IS" and "As Available" Basis</h3>
            <p>THE APP IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO:</p>
            <ul>
              <li>Warranties of merchantability or fitness for a particular purpose</li>
              <li>Warranties of accuracy, reliability, or completeness</li>
              <li>Warranties that the App will be error-free, uninterrupted, or secure</li>
              <li>Warranties that defects will be corrected</li>
            </ul>
          </div>

          <div className="subsection">
            <h3>6.6 Limitation of Liability</h3>
            <p>TO THE MAXIMUM EXTENT PERMITTED BY LAW, RESEARCH METHODOLOGY WIZARD, ITS OWNERS, EMPLOYEES, AND AFFILIATES SHALL NOT BE LIABLE FOR:</p>
            <ul>
              <li>Any indirect, incidental, consequential, special, or punitive damages</li>
              <li>Loss of profits, revenue, data, or use</li>
              <li>Business interruption or loss of opportunity</li>
              <li>Damages arising from your use or inability to use the App</li>
              <li>Damages resulting from reliance on the App's methodological content</li>
            </ul>
            <p><strong>IN NO EVENT SHALL OUR TOTAL LIABILITY EXCEED THE AMOUNT YOU PAID TO US IN THE 12 MONTHS PRECEDING THE CLAIM.</strong></p>
            <p>Some jurisdictions do not allow the exclusion of certain warranties or limitation of liability, so the above limitations may not apply to you.</p>
          </div>
        </section>

        <section className="legal-section">
          <div className="section-header-icon">
            <Scale size={24} />
            <h2>7. Indemnification</h2>
          </div>
          <p>You agree to indemnify, defend, and hold harmless Research Methodology Wizard, its officers, directors, employees, and agents from and against any claims, liabilities, damages, losses, costs, or expenses (including reasonable attorneys' fees) arising out of or related to:</p>
          <ul>
            <li>Your use of the App</li>
            <li>Your violation of these Terms</li>
            <li>Your violation of any rights of third parties</li>
            <li>Your research activities or methodological choices</li>
            <li>Your Content or any content you submit through the App</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>8. Privacy and Data Protection</h2>
          <p>Your use of the App is also governed by our Privacy Policy, which explains how we collect, use, and protect your personal information. By using the App, you consent to our data practices as described in the Privacy Policy.</p>
        </section>

        <section className="legal-section">
          <div className="section-header-icon">
            <Globe size={24} />
            <h2>9. Third-Party Services and Links</h2>
          </div>
          
          <div className="subsection">
            <h3>9.1 Third-Party Integrations</h3>
            <p>The App may integrate with or provide access to third-party services (e.g., citation managers, statistical software, payment processors). Your use of such services is subject to their respective terms and privacy policies. We are not responsible for third-party services or their content.</p>
          </div>

          <div className="subsection">
            <h3>9.2 External Links</h3>
            <p>The App may contain links to external websites for reference or educational purposes. We do not endorse or control these websites and are not responsible for their content, privacy practices, or terms of use.</p>
          </div>
        </section>

        <section className="legal-section">
          <div className="section-header-icon">
            <RefreshCw size={24} />
            <h2>10. Changes to the App and These Terms</h2>
          </div>

          <div className="subsection">
            <h3>10.1 Changes to the App</h3>
            <p>We continuously improve and update Research Methodology Wizard. We reserve the right to:</p>
            <ul>
              <li>Add, modify, or remove features at any time</li>
              <li>Change the design, layout, or functionality</li>
              <li>Discontinue any aspect of the App</li>
            </ul>
            <p>We will make reasonable efforts to notify you of significant changes, but we are not obligated to do so.</p>
          </div>

          <div className="subsection">
            <h3>10.2 Changes to These Terms</h3>
            <p>We may update these Terms from time to time to reflect:</p>
            <ul>
              <li>Changes in our services or features</li>
              <li>Legal or regulatory requirements</li>
              <li>Security or operational improvements</li>
            </ul>
            <p>When we make material changes:</p>
            <ul>
              <li>We will update the "Last Updated" date</li>
              <li>We will notify you via email or prominent notice in the App</li>
              <li>Changes will take effect 30 days after notification</li>
              <li>Your continued use of the App after the effective date constitutes acceptance</li>
            </ul>
            <p>If you do not agree to the updated Terms, you must stop using the App and may close your account.</p>
          </div>
        </section>

        <section className="legal-section">
          <div className="section-header-icon">
            <Scale size={24} />
            <h2>11. Dispute Resolution and Governing Law</h2>
          </div>

          <div className="subsection">
            <h3>11.1 Governing Law</h3>
            <p>These Terms shall be governed by and construed in accordance with the laws of the United Kingdom, without regard to its conflict of law provisions.</p>
          </div>

          <div className="subsection">
            <h3>11.2 Dispute Resolution</h3>
            <p><strong>Informal Resolution:</strong> Before initiating formal proceedings, you agree to contact us at <a href="mailto:researchmethodologywizard@gmail.com">researchmethodologywizard@gmail.com</a> to attempt to resolve any disputes informally.</p>
          </div>

          <div className="subsection">
            <h3>11.3 Class Action Waiver</h3>
            <p>You agree to resolve disputes with us on an individual basis and waive the right to participate in class actions or representative proceedings, to the extent permitted by law.</p>
          </div>
        </section>

        <section className="legal-section">
          <h2>12. General Provisions</h2>

          <div className="subsection">
            <h3>12.1 Entire Agreement</h3>
            <p>These Terms, together with our Privacy Policy and any Additional Terms, constitute the entire agreement between you and Research Methodology Wizard regarding the App.</p>
          </div>

          <div className="subsection">
            <h3>12.2 Severability</h3>
            <p>If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions will remain in full force and effect.</p>
          </div>

          <div className="subsection">
            <h3>12.3 Waiver</h3>
            <p>Our failure to enforce any provision of these Terms shall not constitute a waiver of that provision or any other provision.</p>
          </div>

          <div className="subsection">
            <h3>12.4 Assignment</h3>
            <p>You may not assign or transfer your rights or obligations under these Terms without our prior written consent. We may assign these Terms to any successor or affiliate.</p>
          </div>

          <div className="subsection">
            <h3>12.5 Force Majeure</h3>
            <p>We shall not be liable for any failure or delay in performance due to circumstances beyond our reasonable control, including natural disasters, war, terrorism, labor disputes, or internet failures.</p>
          </div>

          <div className="subsection">
            <h3>12.6 Export Control</h3>
            <p>You agree not to use the App in any country or jurisdiction where such use would violate export control laws or trade sanctions.</p>
          </div>
        </section>

        <section className="legal-section contact-section">
          <div className="section-header-icon">
            <Mail size={24} />
            <h2>13. Contact Information</h2>
          </div>
          
          <p>If you have questions about these Terms, please contact us:</p>
          
          <div className="contact-info">
            <p><strong>Email:</strong> <a href="mailto:researchmethodologywizard@gmail.com">researchmethodologywizard@gmail.com</a></p>
            <p><strong>Support:</strong> <a href="mailto:researchmethodologywizard@gmail.com">researchmethodologywizard@gmail.com</a></p>
          </div>
        </section>
      </div>
    </div>
  );
};

export default TermsOfUse;
